import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, Check, AlertTriangle, ChevronRight } from "lucide-react";
import { Layout } from "@/components/site/Layout";
import { QuotationCTA } from "@/components/site/Shared";
import { useSEO } from "@/hooks/useSEO";
import { divisionBySlug, serviceSlug, IMAGES } from "@/data/catalog";
import { api } from "@/lib/api";

export default function ServiceDivision() {
  const { slug } = useParams();
  const division = divisionBySlug(slug);
  const [projects, setProjects] = useState([]);

  useSEO({
    title: division ? `${division.title} Singapore` : "Services",
    description: division
      ? `${division.title} in Singapore by WAAN METAL. ${division.intro}`
      : "Our services.",
  });

  useEffect(() => {
    if (division) api.get("/projects", { params: { category: division.projectCategory } }).then(({ data }) => setProjects(data)).catch(() => {});
  }, [division]);

  if (!division) return <Navigate to="/" replace />;

  return (
    <Layout>
      <section className="relative bg-[#0A1128] py-20 lg:py-28 overflow-hidden" data-testid="division-hero">
        <div className="absolute inset-0"><img src={division.image} alt="" className="w-full h-full object-cover opacity-25" /><div className="absolute inset-0 bg-gradient-to-br from-[#0A1128] via-[#0A1128]/90 to-[#1E293B]/70" /></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center gap-1.5 text-xs text-slate-400 mb-5">
            <Link to="/" className="hover:text-[#D97706]">Home</Link><ChevronRight size={13} /><span className="text-slate-300">{division.title}</span>
          </nav>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-white tracking-tight max-w-3xl">{division.title}</h1>
          <p className="mt-5 text-slate-300 max-w-2xl text-lg leading-relaxed">{division.intro}</p>
          <Link to="/request-quotation" data-testid="division-quotation-cta" className="mt-8 inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-gradient-to-r from-[#B87333] to-[#D97706] text-[#0A1128] font-bold hover:-translate-y-0.5 transition-all">
            Request a Quotation <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      {division.regulated && (
        <div className="bg-amber-50 border-y border-amber-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex gap-3 text-sm text-amber-900">
            <AlertTriangle size={18} className="shrink-0 mt-0.5 text-amber-600" />
            <p>Structural, electrical, fire-safety and other regulated works are carried out subject to the required approvals and by qualified, licensed professionals.</p>
          </div>
        </div>
      )}

      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="eyebrow mb-3">Services Offered</p>
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-10">Explore {division.title.toLowerCase()} services</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {division.services.map((s) => (
              <Link
                key={s}
                to={`/services/${division.slug}/${serviceSlug(s)}`}
                data-testid={`service-link-${serviceSlug(s)}`}
                className="group p-6 rounded-xl border border-slate-200 hover:border-[#B87333] hover:shadow-lg transition-all flex items-start justify-between gap-3"
              >
                <div>
                  <div className="w-9 h-9 rounded-lg bg-[#B87333]/10 flex items-center justify-center text-[#B87333] mb-3"><Check size={18} /></div>
                  <h3 className="font-semibold text-slate-900 group-hover:text-[#B87333] transition-colors">{s}</h3>
                </div>
                <ArrowRight size={18} className="text-slate-300 group-hover:text-[#B87333] group-hover:translate-x-1 transition-all shrink-0 mt-1" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {projects.length > 0 && (
        <section className="py-16 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-8">Related projects</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {projects.slice(0, 3).map((p) => (
                <div key={p.id} className="rounded-2xl overflow-hidden bg-white shadow-sm">
                  <div className="h-48 bg-slate-200">{p.image_url && <img src={p.image_url} alt={p.title} className="w-full h-full object-cover" />}</div>
                  <div className="p-5"><h3 className="font-semibold text-slate-900">{p.title}</h3>{p.location && <p className="text-sm text-slate-500">{p.location}</p>}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <QuotationCTA />
    </Layout>
  );
}
